/**
 * Página /tesoreria/acceso-wifi — operación de accesos wifi (F5).
 *
 * Contenedor: hace el fetch y delega el render a `PanelAccesoWifi`, el
 * mismo componente validado en el preview dev-only `/dev/acceso-wifi`.
 *
 * Doctrina (convención #17):
 * - Ejecución ASISTIDA: los botones los aprieta una persona.
 * - `puede_operar` viene del backend según el rol: tesorero ejecuta,
 *   comisión solo mira. No se decide en el frontend.
 * - Si el backend está en modo `simulacion`, se avisa arriba: los botones
 *   no ejecutarían nada.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../lib/api/client';
import {
  activarAccesoWifi,
  completarAccesoWifi,
  volverACompartirAccesoWifi,
  getConciliacionWifi,
  revocarAccesoWifi,
  reprogramarCorreo,
  revocarSinTicket,
  type ConciliacionWifiRead,
  type FilaAccesoWifiRead,
  type ResultadoOperacion,
} from '../../lib/api/acceso-wifi';
import {
  PanelAccesoWifi,
  type AccesoWifiFila,
  type DatosAccesoWifi,
  type HuerfanoEWeLink,
} from '../../components/tesoreria/PanelAccesoWifi';
import { ModalConfirmar } from '../../components/ui/ModalConfirmar';
import { formatearFechaHora } from '../../lib/tiempo';

function aFila(f: FilaAccesoWifiRead): AccesoWifiFila {
  return {
    acceso_id: f.acceso_id,
    lote_identificador: f.lote_identificador,
    residente_nombre: f.residente_nombre,
    parentesco: f.parentesco,
    email: f.email,
    estado: f.estado,
    alta_ewelink_at: f.alta_ewelink_at
      ? formatearFechaHora(f.alta_ewelink_at)
      : null,
    error: f.error,
    comment: f.comment,
    comment_consistente: f.comment_consistente,
    comments_en_ewelink: f.comments_en_ewelink,
    email_actual: f.email_actual,
  };
}

function aDatos(c: ConciliacionWifiRead): DatosAccesoWifi {
  return {
    porActivar: c.por_activar.map(aFila),
    porDarDeBaja: c.por_dar_de_baja.map(aFila),
    activos: c.activos.map(aFila),
    sinAccesoReal: c.sin_acceso_real.map(aFila),
    correoCambiado: c.correo_cambiado.map(aFila),
    huerfanos: c.huerfanos.map((h) => ({
      email: h.email,
      apikey: h.apikey,
      comment: h.comentario,
      compartido_desde: h.compartido_desde
        ? formatearFechaHora(h.compartido_desde)
        : null,
      lote_identificador: h.lote_identificador,
      nombre_padron: h.nombre_padron,
    })),
    incompletos: c.incompletos ?? [],
    nombresCanal: c.nombres_canal ?? {},
    ocupacion: c.ocupacion,
    conexion: {
      ok: c.conexion.ok,
      mensaje: c.conexion.mensaje,
      consultado_at: c.conexion.consultado_at
        ? formatearFechaHora(c.conexion.consultado_at)
        : null,
    },
  };
}

export function AccesoWifiPage() {
  const [datos, setDatos] = useState<ConciliacionWifiRead | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ejecutando, setEjecutando] = useState(false);
  // Fila (o filas) en curso: el botón muestra "Activando…" en vez de un
  // spinner global, así se ve CUÁL se está procesando.
  const [enCurso, setEnCurso] = useState<string | null>(null);
  // Confirmación pendiente para las acciones destructivas. `window.confirm`
  // está prohibido en el proyecto: se usa el modal propio.
  const [porConfirmar, setPorConfirmar] = useState<{
    title: string;
    mensaje: string;
    textoConfirmar: string;
    ejecutar: () => Promise<void>;
  } | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      setDatos(await getConciliacionWifi());
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : 'No pudimos cargar los accesos wifi.',
      );
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  /**
   * Ejecuta N operaciones y resume el resultado.
   *
   * Van SECUENCIALES y no en paralelo: cada una hace varias llamadas a
   * eWeLink (listar + compartir/revocar por canal) y el proveedor no
   * responde bien a ráfagas. Con 47 huérfanos, en paralelo serían cientos
   * de llamadas simultáneas.
   */
  async function ejecutarLote(
    etiqueta: string,
    tareas: (() => Promise<ResultadoOperacion>)[],
    idEnCurso?: string,
  ) {
    if (tareas.length === 0 || ejecutando) return;
    setEjecutando(true);
    if (idEnCurso) setEnCurso(idEnCurso);
    setError(null);
    setAviso(null);
    let ok = 0;
    let simulados = 0;
    const fallos: string[] = [];
    for (const tarea of tareas) {
      try {
        const r = await tarea();
        if (r.ok) ok += 1;
        else fallos.push(r.mensaje);
        if (r.simulado) simulados += 1;
      } catch (e) {
        fallos.push(
          e instanceof ApiError ? e.message : 'Error de red',
        );
      }
    }
    // El modo simulación se dice SIEMPRE y primero: un "listo" que no tocó
    // nada es peor que un error.
    const prefijo =
      simulados > 0 ? 'SIMULADO (no se ejecutó nada): ' : '';
    if (fallos.length === 0) {
      setAviso(`${prefijo}${etiqueta}: ${ok} de ${tareas.length}.`);
    } else {
      setError(
        `${prefijo}${etiqueta}: ${ok} de ${tareas.length}. ` +
          `Falló ${fallos.length}: ${[...new Set(fallos)].join(' · ')}`,
      );
    }
    setEjecutando(false);
    setEnCurso(null);
    await cargar();
  }



  return (
    <div className="space-y-4">
      {error && (
        <div
          role="alert"
          className="rounded-md border border-state-danger bg-state-danger/10 p-3 text-sm text-state-danger"
        >
          {error}
        </div>
      )}

      {aviso && (
        <div className="rounded-md border border-state-success bg-state-success/10 p-3 text-sm text-state-success">
          {aviso}
        </div>
      )}

      {/* Un "Activar" que no activa es peor que un botón deshabilitado: si
          el backend está en simulación, se dice antes de mostrar nada. */}
      {datos?.simulado && (
        <div className="rounded-md border border-state-warning bg-state-warning/10 p-3">
          <p className="text-sm font-bold text-state-warning">
            Modo simulación — las acciones no se ejecutan
          </p>
          <p className="text-xs text-text-secondary mt-0.5">
            El sistema lee el estado real de eWeLink, pero activar o dar de
            baja no se aplica todavía. Lo habilita el super_admin cambiando{' '}
            <code className="font-mono">EWELINK_BACKEND</code> a{' '}
            <code className="font-mono">real</code>.
          </p>
        </div>
      )}

      {datos && (
        <PanelAccesoWifi
          datos={aDatos(datos)}
          puedeOperar={datos.puede_operar}
          onRefrescar={() => void cargar()}
          onActivar={(filas) => {
            // Sin confirmación: activar no es destructivo — le DA acceso a
            // alguien que la Comisión ya autorizó. El feedback es el botón
            // en "Activando…" y la fila pasando a Activos al refrescar.
            void ejecutarLote(
              'Activados',
              filas.map((f) => () => activarAccesoWifi(f.acceso_id)),
              filas.length === 1 ? filas[0].acceso_id : undefined,
            );
          }}
          enCurso={enCurso}
          ejecutando={ejecutando}
          onDarDeBaja={(filas) => {
            setPorConfirmar({
              title: 'Dar de baja el acceso wifi',
              mensaje:
                `Se les quitará el acceso a los portones a ${filas.length} ` +
                'residente(s). Para devolverlo, el titular tendrá que ' +
                'volver a marcar la casilla.',
              textoConfirmar: 'Dar de baja',
              ejecutar: () =>
                ejecutarLote(
                  'Dados de baja',
                  filas.map(
                    (f) => () =>
                      revocarAccesoWifi(f.acceso_id, 'baja desde Tesorería'),
                  ),
                  filas.length === 1 ? filas[0].acceso_id : undefined,
                ),
            });
          }}
          onCompletar={(item) => {
            // Sin confirmación, igual que Activar: le DA el acceso que le
            // falta a alguien ya autorizado; no quita nada.
            if (!item.acceso_id) return;
            const id = item.acceso_id;
            void ejecutarLote('Completados', [() => completarAccesoWifi(id)], id);
          }}
          onVolverACompartir={(filas) => {
            // Sin confirmación, igual que Activar: repone un acceso ya
            // autorizado que alguien quitó por fuera; no quita nada.
            void ejecutarLote(
              'Compartidos de nuevo',
              filas.map((f) => () => volverACompartirAccesoWifi(f.acceso_id)),
              filas.length === 1 ? filas[0].acceso_id : undefined,
            );
          }}
          onReprogramarCorreo={(filas) => {
            setPorConfirmar({
              title: 'Reprogramar el correo del acceso',
              mensaje:
                `Se quitará el acceso del correo anterior y se compartirá con ` +
                `el nuevo, en ese orden — al revés, si la revocación falla ` +
                `quedarían dos correos abriendo el portón. ` +
                `${filas.length} acceso(s). Si el correo nuevo todavía no ` +
                'tiene cuenta en eWeLink, el acceso queda en error y el ' +
                'vecino tendrá que crearla.',
              textoConfirmar: 'Reprogramar',
              ejecutar: () =>
                ejecutarLote(
                  'Reprogramados',
                  filas.map((f) => () => reprogramarCorreo(f.acceso_id)),
                  filas.length === 1 ? filas[0].acceso_id : undefined,
                ),
            });
          }}
          onEliminarDeEwelink={(items: HuerfanoEWeLink[]) => {
            setPorConfirmar({
              title: 'Eliminar de eWeLink',
              mensaje:
                `${items.length} acceso(s) sin ticket en el portal. Si ` +
                'alguno es un vecino que sí debe tener acceso, tendrá que ' +
                'solicitarlo de nuevo desde su portal.',
              textoConfirmar: 'Eliminar de eWeLink',
              ejecutar: () =>
                ejecutarLote(
                  'Eliminados de eWeLink',
                  items.map(
                    (h) => () =>
                      revocarSinTicket(
                        h.email,
                        h.apikey,
                        'sin ticket en el portal',
                      ),
                  ),
                ),
            });
          }}
        />
      )}

      {!datos && cargando && (
        <p className="text-sm text-text-secondary">Cargando…</p>
      )}

      {porConfirmar && (
        <ModalConfirmar
          title={porConfirmar.title}
          mensaje={porConfirmar.mensaje}
          textoConfirmar={porConfirmar.textoConfirmar}
          variantConfirmar="danger"
          onConfirmar={async () => {
            const accion = porConfirmar.ejecutar;
            setPorConfirmar(null);
            await accion();
          }}
          onCancelar={() => setPorConfirmar(null)}
        />
      )}
    </div>
  );
}
